import { DateTime } from '@mode-switch/express';
import { EncodingResult } from '@mode-vegas/vegas';

import { ChartTypes } from '@mode/shared/contract-common';

// AnalyticsProps is a type only. Ignoring this boundary for now.
// eslint-disable-next-line @nx/enforce-module-boundaries
import { AnalyticsEvent, AnalyticsProps } from '@mode/shared/util-segment';

// These are actions passed to/from the report iframe. Not to be used in the redux/ngrx store.
export enum EmbedActionType {
  UpdateEncoding = 'UpdateEncoding',
  ChangeHierarchy = 'ChangeHierarchy',
  DrillDown = 'DrillDown',
  DynamicDrillDown = 'DynamicDrillDown',
  Forward = 'HistoryForward',
  Back = 'HistoryBack',

  ClickChartMark = 'ClickChartMark',
  ClickDrilldownInterval = 'ClickDrilldownInterval',
  ClickViewData = 'ClickViewData',
  ClickExploreFromHere = 'ClickExploreFromHere',

  Keep = 'Keep',
  Exclude = 'Exclude',
  ViewData = 'ViewData',

  ShowAlert = 'ShowAlert',
  ExploreChart = 'ExploreChart',
  ExploreChartMark = 'ExploreChartMark',
  ExpandKebab = 'ExpandKebab',

  ChartRendered = 'ChartRendered',
  NotebookVisualizationRendered = 'NotebookVisualizationRendered',
  TableRendered = 'TableRendered',
  MuzeChartRendered = 'MuzeChartRendered',
  CopyChart = 'CopyChart',

  AnalyticsEvent = 'AnalyticsEvent',
}

export interface BackAction {
  type: EmbedActionType.Back;
  chartToken: string;
}

export interface ForwardAction {
  type: EmbedActionType.Forward;
  chartToken: string;
}

export interface ClickChartMark {
  type: EmbedActionType.ClickChartMark;
  chartToken: string;
  props: AnalyticsProps;
}

export interface ClickDrilldownInterval {
  type: EmbedActionType.ClickDrilldownInterval;
  chartToken: string;
  props: AnalyticsProps;
}

export interface ClickViewData {
  type: EmbedActionType.ClickViewData;
  chartToken: string;
  props: AnalyticsProps;
}

export interface ClickExploreFromHere {
  type: EmbedActionType.ClickExploreFromHere;
  chartToken: string;
  props: AnalyticsProps;
}

export interface DrillDownAction {
  type: EmbedActionType.DrillDown;
  target: ChartTypes.MarkTarget;
  chartToken: string;
}

export interface DynamicDrillDownAction {
  type: EmbedActionType.DynamicDrillDown;
  target: ChartTypes.MarkTarget;
  drillToField: string;
  targetFieldId: string;
  chartToken: string;
  dateTruncUnit?: DateTime.Unit;
}

export interface ChangeHierarchyAction {
  type: EmbedActionType.ChangeHierarchy;
  forward: boolean;
  chartToken: string;
  props: AnalyticsProps;
}

export interface UpdateEncoding {
  type: EmbedActionType.UpdateEncoding;
  chartToken: string;
  encResult: EncodingResult;
}

export interface ViewDataAction {
  type: EmbedActionType.ViewData;
  target: ChartTypes.MarkTarget;
  chartToken: string;
  queryToken: string;
}

export interface ShowAlertAction {
  type: EmbedActionType.ShowAlert;
  success: boolean;
  message: string;
}

export interface ExploreChartAction {
  type: EmbedActionType.ExploreChart;
  chartToken: string;
  props: AnalyticsProps;
}

export interface ExpandKebabAction {
  type: EmbedActionType.ExpandKebab;
  chartToken: string;
  props: AnalyticsProps;
}

export interface ExploreChartMarkAction {
  type: EmbedActionType.ExploreChartMark;
  target: ChartTypes.MarkTarget;
  filters: ChartTypes.Filter[];
  chartToken: string;
}

export interface ChartRenderedAction {
  type: EmbedActionType.ChartRendered;
  token: string;
  visualizationRendered: boolean; // Refers to visualiztion with graph appearing as opposed to rendered visualization w/o graph e.g. invalid or incomplete configuration provided
  duration: number;
  chartType: string;
  options?: any;
  timestamp: number;
}

export interface MuzeChartRenderedAction {
  type: EmbedActionType.MuzeChartRendered;
  token: string;
  dataCount: number;
  schemaCount: number;
  rowCount: number;
  columnCount: number;
  facetCount: number;
  duration: number;
}

export interface NotebookVisualizationRenderedAction {
  type: EmbedActionType.NotebookVisualizationRendered;
  token: string;
  visualizationRendered: boolean; // Refers to visualiztion with graph appearing as opposed to rendered visualization w/o graph e.g. invalid or incomplete configuration provided
  duration: number;
  timestamp: number;
  chartType: string;
  options?: any;
}

export interface TableRenderedAction {
  type: EmbedActionType.TableRendered;
  token: string;
}
export interface CopyChartAction {
  type: EmbedActionType.CopyChart;
  token: string;
}

export interface AnalyticsEventAction {
  type: EmbedActionType.AnalyticsEvent;
  name: AnalyticsEvent.Name;
  location: string;
  action: string;
  context: string;
  context_id: string;
  chartType: string;
}

export type EmbedActions =
  | UpdateEncoding
  | BackAction
  | ForwardAction
  | ClickChartMark
  | ClickDrilldownInterval
  | ClickViewData
  | ClickExploreFromHere
  | ViewDataAction
  | DrillDownAction
  | DynamicDrillDownAction
  | ChangeHierarchyAction
  | ShowAlertAction
  | ExploreChartAction
  | ExploreChartMarkAction
  | ExpandKebabAction
  | ChartRenderedAction
  | NotebookVisualizationRenderedAction
  | TableRenderedAction
  | MuzeChartRenderedAction
  | CopyChartAction
  | AnalyticsEventAction;
